import React from 'react';

import { Typography, ThemeProvider, StyledEngineProvider } from '@mui/material';
import { ProgressBar } from '../../Components/ProgressBar/ProgressBar';
import { TemplateBox } from './TemplateBox';
import { ImportDownload } from '../../Components/Icons/ImportDownload';
import { importPageStyles, getMuiTheme } from './Import.styles';
import { useTranslation } from 'react-i18next';
import { UploadBox } from './UploadBox';
import MUIDataTable, { MUIDataTableOptions } from 'mui-datatables';
import { useImport } from './useImport';
import { ImportList } from './ImportList';
import { Button } from '../../UIKit';
import StartImportModal from '../Events/EventPage/Components/Modals/StartImportModal.component';
import { IImportResponse } from '../../Models/DTO/Import.model';
import { ValidationSwitch } from './ValidationSwitch';
import { Route } from 'react-router-dom';
import { Route as NeoRoutes } from '~/Constants/Routes.constants';
import { useNavigation } from '../../Hooks';
import { PrivateLayout } from '~/Layout/PrivateLayout';
import { Error } from '~/Components';

export const Import = () => {
  const {
    currentStep,
    uploadStatus,
    title,
    tableData,
    headers,
    showProblems,
    setShowProblems,
    modalOpen,
    setModalOpen,
    error,
  } = useImport();

  const { t } = useTranslation();
  const { navigate } = useNavigation();
  const { classes } = importPageStyles();

  const steps = [
    {
      index: 1,
      label: t('app.import.upload'),
    },
    {
      index: 2,
      label: t('app.import.review'),
    },
    {
      index: 3,
      label: t('app.import'),
    },
  ];

  const columns = headers.map((header) => ({
    name: `${header[0].toLowerCase()}${header.substring(1)}`,
    label: header,
    options: {
      customBodyRender: (value: IImportResponse, tableMeta: any) => {
        const validationData = tableMeta.rowData[tableMeta.rowData.length - 1];
        const hasError =
          validationData && validationData.some((err: any) => err.columnName === header);
        return (
          <div className={`${hasError && classes.cellError} ${classes.cell}`}>
            {value}
            {hasError && (
              <div className={classes.cellErrorText}>{t(`import.${validationData[0].error}`)}</div>
            )}
          </div>
        );
      },
      display: header !== 'validationErrors',
    },
  }));

  const options: MUIDataTableOptions = {
    download: false,
    print: false,
    viewColumns: false,
    filter: false,
    // eslint-disable-next-line react/no-multi-comp
    customToolbar: () => (
      <ValidationSwitch showProblems={showProblems} setShowProblems={setShowProblems} />
    ),
    selectableRows: 'none',
    search: false,
    textLabels: {
      pagination: t('app.import.pagination.of'),
      selectedRows: t('app.import.pagination.selectedRows'),
    },
  };

  return (
    <PrivateLayout title={t('app.importData')} hideBackButton={currentStep === 0}>
      <div className={classes.container}>
        <Error error={error} />
        <div className={classes.progressBar}>
          <ProgressBar currentStep={currentStep} steps={steps} />
        </div>
        <Typography className={classes.subHeader} variant="h5">
          {t(`${uploadStatus === 'FileParsed' ? 'app.import.reviewData' : 'app.import.newImport'}`)}
        </Typography>
        {uploadStatus === 'FileParsingFailed' && (
          <div className={classes.error}>{t('app.import.error')}</div>
        )}
        <Route exact path={[NeoRoutes.IMPORT, NeoRoutes.IMPORT_COMPLETE]}>
          <div className={classes.importUploadSection}>
            <div className={classes.box}>
              <TemplateBox
                buttonText={t('app.import.downloadTemplate')}
                description={t('app.import.downloadTemplate.info')}
                icon={<ImportDownload />}
                title={t('app.import.downloadTemplate')}
                buttonType="downloadTemplate"
              />
            </div>
            <UploadBox className={classes.box} />
          </div>
          <ImportList />
        </Route>
        <Route exact path={NeoRoutes.IMPORT_REVIEW}>
          <div className={classes.importReviewWrapper}>
            <div className={classes.tableWrapper}>
              <StyledEngineProvider injectFirst>
                <ThemeProvider theme={getMuiTheme}>
                  <MUIDataTable
                    title={title}
                    data={tableData}
                    columns={columns}
                    options={options}
                  />
                </ThemeProvider>
              </StyledEngineProvider>
              <StartImportModal open={modalOpen} onClose={() => setModalOpen(false)} />
            </div>
            <Button
              onClick={() => setModalOpen(true)}
              color="primary"
              variant="contained"
              className={classes.modalButton}
            >
              {t('app.event.completeImport')}
            </Button>
            <Button onClick={() => navigate(NeoRoutes.IMPORT)} className={classes.modalButton}>
              {t('app.cancel')}
            </Button>
          </div>
        </Route>
      </div>
    </PrivateLayout>
  );
};
export default Import;
